import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, CheckCircle, Schedule } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import { selectCurrentUser } from "../../redux/reducers/authSlice";
import {
  ReviewStep,
  useGetComsBlockByIdQuery,
  useGetComsWeeklyReportByIdQuery,
  useGetComsWeeklyReportsQuery,
  useUpdateComsWeeklyReportMutation,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";
// C3: Only Site QS and Site Manager are required to sign off
const ROLES: ReviewStep["role"][] = ["Site QS", "Site Manager"];

const ROLE_LABEL: Record<string, string> = {
  "Site QS": "Quantity Surveyor (QS)",
  "Site Manager": "Site Manager",
};
const roleLabel = (role: string) => ROLE_LABEL[role] ?? role;

const STATUS_LABEL: Record<string, string> = {
  "awaiting-site-qs": "Awaiting Quantity Surveyor (QS)",
  "awaiting-site-manager": "Awaiting Site Manager",
  approved: "Approved",
};

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success"> =
  {
    "awaiting-site-qs": "warning",
    "awaiting-site-manager": "warning",
    approved: "success",
  };

const fmt = (v?: number) =>
  `$${(v ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtN = (v?: number, dp = 2) => (v ?? 0).toFixed(dp);

// ─── Section helpers ────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ bgcolor: COMS_NAVY, px: 3, py: 1.5, borderRadius: "8px 8px 0 0" }}>
    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
      {title}
    </Typography>
  </Box>
);

const MetricBox: React.FC<{
  label: string;
  value: string;
  color?: string;
  sub?: string;
}> = ({ label, value, color = COMS_NAVY, sub }) => (
  <Box
    sx={{
      flex: "1 1 160px",
      px: 3,
      py: 2.5,
      textAlign: "center",
      borderRight: "1px solid #f0f0f0",
      "&:last-child": { borderRight: "none" },
    }}
  >
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700, color, fontSize: "1.15rem" }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Box>
);

// ─── Main component ──────────────────────────────────────────────────────────

interface Props {
  blockId: string;
}

const WeeklyReportDetail: React.FC<Props> = ({ blockId }) => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);

  const { data: report, isLoading } = useGetComsWeeklyReportByIdQuery(
    reportId ?? "",
    { skip: !reportId },
  );
  const { data: weeklyReports = [] } = useGetComsWeeklyReportsQuery(blockId);
  const { data: block } = useGetComsBlockByIdQuery(blockId);
  const [updateReport, { isLoading: isSaving }] =
    useUpdateComsWeeklyReportMutation();

  // Local comment drafts per role index
  const [comments, setComments] = useState<Record<number, string>>({});

  const reportWeekStart = report?.weekStart ?? "";
  const cumulativeProfitFromHistory = useMemo(() => {
    const reportYear = reportWeekStart ? Number(reportWeekStart.slice(0, 4)) : 0;
    const juneStart = reportYear > 0 ? `${reportYear}-06-01` : "";
    const sorted = [...weeklyReports].sort((a, b) =>
      (a.weekStart ?? "").localeCompare(b.weekStart ?? ""),
    );
    let sum = 0;
    for (const r of sorted) {
      const weekStart = r.weekStart ?? "";
      if (
        weekStart <= reportWeekStart &&
        (!juneStart || weekStart >= juneStart)
      ) {
        sum += Number(r.periodProfitLoss) || 0;
      }
    }
    return (Number(block?.profitStartingBaseline) || 0) + sum;
  }, [block?.profitStartingBaseline, weeklyReports, reportWeekStart]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, color: COMS_NAVY, mb: 1 }}>
            Weekly report not found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This report may have been regenerated and the old link is no longer valid.
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              navigate(`/coms/daily-site-report/blocks/${blockId}/weekly-reports`)
            }
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            Back To Weekly Reports
          </Button>
        </Paper>
      </Box>
    );
  }

  const trail: ReviewStep[] = (report.reviewTrail as ReviewStep[]) ?? [];

  // Index of first pending step
  const pendingIndex = trail.findIndex((s) => s.decision === "pending");

  const periodWorkPct =
    (report.bac ?? 0) > 0
      ? (((report.periodWorkDoneTotal ?? 0) / (report.bac ?? 0)) * 100)
      : 0;
  const cumulativeWorkPct =
    (report.bac ?? 0) > 0 ? (((report.ev ?? 0) / (report.bac ?? 0)) * 100) : 0;

  const cumulativeProfitDisplay = cumulativeProfitFromHistory;

  const rowPeriodPct = (item: any) => {
    if (Number.isFinite(item?.periodPct)) return Number(item.periodPct);
    const plannedAmount = Number(item?.plannedAmount) || 0;
    const itemAmount = Number(item?.totalAmount) || 0;
    return plannedAmount > 0 ? (itemAmount / plannedAmount) * 100 : null;
  };

  const rowCumulativePct = (item: any) => {
    if (Number.isFinite(item?.cumulativePct)) return Number(item.cumulativePct);
    const plannedAmount = Number(item?.plannedAmount) || 0;
    const cumulativeAmount = Number(item?.cumulativeAmount) || 0;
    if (plannedAmount > 0 && cumulativeAmount > 0) {
      return (cumulativeAmount / plannedAmount) * 100;
    }
    return rowPeriodPct(item);
  };

  const handleSubmit = async (idx: number) => {
    const newTrail = trail.map((s, i) =>
      i === idx
        ? {
            ...s,
            comment: comments[idx] ?? "",
            decision: "approved" as const,
            submittedAt: new Date().toISOString(),
            userName: currentUser?.name ?? null,
          }
        : s,
    );
    await updateReport({ id: report.id!, reviewTrail: newTrail });
    setComments((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() =>
            navigate(`/coms/daily-site-report/blocks/${blockId}/weekly-reports`)
          }
          sx={{ color: COMS_NAVY }}
        >
          All Weekly Reports
        </Button>
        <Divider orientation="vertical" flexItem />
        <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 600 }}>
          Week {report.weekStart} → {report.weekEnd}
        </Typography>
        <Chip
          label={STATUS_LABEL[report.status ?? ""] ?? report.status}
          color={STATUS_COLOR[report.status ?? ""] ?? "default"}
        />
      </Box>

      {/* ── Cost Summary ───────────────────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Cost Summary — Period" />
        <Box display="flex" flexWrap="wrap">
          <MetricBox
            label="Revenue (Work Done)"
            value={fmt(report.periodRevenue)}
          />
          <MetricBox
            label="Materials"
            value={fmt(report.periodMaterials)}
            color="#1565c0"
          />
          <MetricBox
            label="Labour"
            value={fmt(report.periodLabour)}
            color="#b71c1c"
          />
          <MetricBox
            label="Equipment"
            value={fmt(report.periodEquipment)}
            color="#bf8c00"
          />
          <MetricBox
            label="Profit / Loss"
            value={fmt(report.periodProfitLoss)}
            color={(report.periodProfitLoss ?? 0) >= 0 ? "#2e7d32" : "#c62828"}
          />
        </Box>
      </Paper>

      {/* ── Work Done ─────────────────────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Work Done in Period" />
        {!report.workDone?.length ? (
          <Box px={3} py={2}>
            <Typography color="text.secondary">
              No activities recorded this week.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                {["Activity", "Unit", "Period Qty", "Amount", "Period %", "Cumulative %"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {report.workDone.map((item: any, i: number) => (
                (() => {
                  const periodPct = rowPeriodPct(item);
                  const cumulativePct = rowCumulativePct(item);
                  return (
                    <TableRow key={i} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>{fmtN(item.totalQuantity)}</TableCell>
                      <TableCell>{fmt(item.totalAmount)}</TableCell>
                      <TableCell sx={{ color: (periodPct ?? 0) > 0 ? "#2e7d32" : undefined }}>
                        {periodPct == null ? "N/A" : `${periodPct.toFixed(1)}%`}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: (cumulativePct ?? 0) > 100 ? "#c62828" : (cumulativePct ?? 0) >= 90 ? "#e65100" : undefined }}>
                        {cumulativePct == null ? "N/A" : `${cumulativePct.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  );
                })()
              ))}
              <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {fmt(report.periodWorkDoneTotal)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#2e7d32" }}>
                  {`${periodWorkPct.toFixed(1)}%`}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color:
                      cumulativeWorkPct > 100
                        ? "#c62828"
                        : cumulativeWorkPct >= 90
                        ? "#e65100"
                        : undefined,
                  }}
                >
                  {`${cumulativeWorkPct.toFixed(1)}%`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Materials Used ────────────────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Materials Used in Period" />
        {!report.materialsUsed?.length ? (
          <Box px={3} py={2}>
            <Typography color="text.secondary">
              No materials recorded this week.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                {["Material", "Unit", "Quantity", "Amount"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {report.materialsUsed.map((item: any, i: number) => (
                <TableRow key={i} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{fmtN(item.totalQuantity)}</TableCell>
                  <TableCell>{fmt(item.totalAmount)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {fmt(report.periodMaterials)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Labour summary only ────────────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Labour Summary" />
        {!report.dailyLabour?.length ? (
          <Box px={3} py={2}>
            <Typography color="text.secondary">
              No labour recorded this week.
            </Typography>
          </Box>
        ) : (
          <>
            {/* C4: Summary by grade */}
            {(() => {
              const gradeMap = new Map<string, { grade: string; totalPeople: number; dayRate: number; totalAmount: number }>();
              for (const group of report.dailyLabour as any[]) {
                for (const l of group.lines as any[]) {
                  const g = l.grade ?? "Unknown";
                  if (!gradeMap.has(g)) gradeMap.set(g, { grade: g, totalPeople: 0, dayRate: l.dayRate ?? 0, totalAmount: 0 });
                  const entry = gradeMap.get(g)!;
                  entry.totalPeople += Number(l.people) || 0;
                  entry.totalAmount += Number(l.amount) || 0;
                }
              }
              const grades = [...gradeMap.values()].sort((a, b) => a.grade.localeCompare(b.grade));
              return (
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", letterSpacing: 0.5 }}>SUMMARY BY GRADE</Typography>
                  <Table size="small" sx={{ mb: 1 }}>
                    <TableHead>
                      <TableRow>
                        {["Grade", "Total Person-Days", "Day Rate", "Total Amount"].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 600, py: 0.5, fontSize: "0.75rem" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grades.map(g => (
                        <TableRow key={g.grade}>
                          <TableCell sx={{ fontWeight: 600 }}>{g.grade}</TableCell>
                          <TableCell>{g.totalPeople}</TableCell>
                          <TableCell>{fmt(g.dayRate)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{fmt(g.totalAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              );
            })()}
          </>
        )}
      </Paper>

      {/* ── Equipment per Day ────────────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Equipment per Day" />
        {!report.dailyEquipment?.length ? (
          <Box px={3} py={2}>
            <Typography color="text.secondary">
              No equipment recorded this week.
            </Typography>
          </Box>
        ) : (
          report.dailyEquipment.map((group: any) => (
            <Box key={group.date}>
              <Box
                sx={{
                  bgcolor: "#f5f7fa",
                  px: 3,
                  py: 0.75,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {group.date}
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Item", "Unit", "Hours", "Hourly Rate", "Amount"].map(
                      (h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, py: 0.75 }}>
                          {h}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.lines.map((e: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{e.name}</TableCell>
                      <TableCell>{e.unit}</TableCell>
                      <TableCell>{fmtN(e.hours)}</TableCell>
                      <TableCell>{fmt(e.hourlyRate)}</TableCell>
                      <TableCell>{fmt(e.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        )}
      </Paper>

      {/* ── Cost Performance (Period) ─────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Cost Performance — This Week" />
        <Box display="flex" flexWrap="wrap">
          <MetricBox label="Revenue" value={fmt(report.periodRevenue)} />
          <MetricBox
            label="Materials"
            value={fmt(report.periodMaterials)}
            color="#1565c0"
          />
          <MetricBox
            label="Labour"
            value={fmt(report.periodLabour)}
            color="#b71c1c"
          />
          <MetricBox
            label="Equipment"
            value={fmt(report.periodEquipment)}
            color="#bf8c00"
          />
          <MetricBox
            label={`${(report.periodProfitLoss ?? 0) >= 0 ? "PROFIT" : "LOSS"}`}
            value={fmt(report.periodProfitLoss)}
            color={(report.periodProfitLoss ?? 0) >= 0 ? "#2e7d32" : "#c62828"}
          />
          <MetricBox
            label={`${cumulativeProfitDisplay >= 0 ? "CUMULATIVE PROFIT" : "CUMULATIVE LOSS"}`}
            value={fmt(cumulativeProfitDisplay)}
            color={cumulativeProfitDisplay >= 0 ? "#2e7d32" : "#c62828"}
          />
        </Box>
        {(report.periodProfitLoss ?? 0) < 0 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="warning" icon={false}>
              This week resulted in a <strong>loss</strong>. Review costs and
              consider corrective action.
            </Alert>
          </Box>
        )}
      </Paper>

      {/* ── Review & Decision Trail ───────────────────────────────────────── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader title="Review & Decision Trail" />
        <Box px={3} py={2}>
          {ROLES.map((role, idx) => {
            const step = trail[idx] as ReviewStep | undefined;
            const approved = step?.decision === "approved";
            const isCurrentStep = idx === pendingIndex;
            const locked = idx > pendingIndex && pendingIndex !== -1;

            return (
              <Box
                key={role}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: isCurrentStep
                    ? `2px solid ${COMS_NAVY}`
                    : "1px solid #e0e0e0",
                  bgcolor: approved ? "#f1f8f1" : locked ? "#fafafa" : "#fff",
                  opacity: locked ? 0.55 : 1,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={approved || isCurrentStep ? 1 : 0}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {approved ? (
                      <CheckCircle sx={{ color: "#2e7d32", fontSize: 20 }} />
                    ) : (
                      <Schedule
                        sx={{
                          color: isCurrentStep ? COMS_NAVY : "#bdbdbd",
                          fontSize: 20,
                        }}
                      />
                    )}
                    <Typography sx={{ fontWeight: 600 }}>
                      {roleLabel(role)}
                    </Typography>
                  </Box>
                  {approved && (
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary">
                        {step?.userName ?? "Unknown"} ·{" "}
                        {step?.submittedAt
                          ? new Date(step.submittedAt).toLocaleString()
                          : ""}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {approved && step?.comment && (
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: "#fff",
                      border: "1px solid #e8e8e8",
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      mt: 0.5,
                    }}
                  >
                    "{step.comment}"
                  </Typography>
                )}

                {isCurrentStep && !approved && (
                  <Box mt={1}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Comment (optional)"
                      value={comments[idx] ?? ""}
                      onChange={(e) =>
                        setComments((prev) => ({
                          ...prev,
                          [idx]: e.target.value,
                        }))
                      }
                      sx={{ mb: 1.5 }}
                    />
                    <Button
                      variant="contained"
                      disabled={isSaving}
                      onClick={() => handleSubmit(idx)}
                      sx={{
                        bgcolor: COMS_NAVY,
                        "&:hover": { bgcolor: "#1a4a7a" },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                    >
                      Approve as {roleLabel(role)}
                    </Button>
                    <Alert
                      severity="info"
                      sx={{ mt: 1.5, py: 0.5 }}
                      icon={false}
                    >
                      Submitting as{" "}
                      <strong>{currentUser?.name ?? "current user"}</strong>.
                      This action is irreversible.
                    </Alert>
                  </Box>
                )}

                {locked && (
                  <Typography variant="caption" color="text.secondary">
                    Awaiting prior approval
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default WeeklyReportDetail;
